import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Store } from '../types';
import { lookupAPI } from '../services/api';

interface StoreSelectionDialogProps {
  open: boolean;
  onStoreSelect: (store: Store) => void;
  selectedStore?: Store | null;
}

const StoreSelectionDialog: React.FC<StoreSelectionDialogProps> = ({
  open,
  onStoreSelect,
  selectedStore,
}) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadStores();
    }
  }, [open]);

  useEffect(() => {
    if (selectedStore && stores.length > 0) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore, stores]);

  const loadStores = async () => {
    setLoading(true);
    setError(null);
    try {
      const storesData = await lookupAPI.getStores();
      setStores(storesData);
      
      // If there's only one store, pre-select it
      if (storesData.length === 1) {
        setSelectedStoreId(storesData[0].id);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('Failed to load stores. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    const store = stores.find(s => s.id === selectedStoreId);
    if (store) {
      onStoreSelect(store);
    }
  };

  const isConfirmDisabled = !selectedStoreId || loading;

  return (
    <Dialog 
      open={open} 
      disableEscapeKeyDown 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" component="div" color="primary" fontWeight="bold">
          Select Your Store
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Choose the store you'll be working with today
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth required>
            <InputLabel>Store</InputLabel>
            <Select
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value as number)}
              label="Store"
              disabled={loading}
            >
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.store_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {stores.length === 0 && !loading && !error && (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
            No stores available
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          size="large"
          disabled={isConfirmDisabled}
          fullWidth
          sx={{ py: 1.5 }}
        >
          {loading ? 'Loading...' : 'Continue with Selected Store'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreSelectionDialog;
